// src/components/setup/steps/BusinessProfile.jsx
import React, { useState, useEffect } from 'react';
import { Building, Upload, X, MapPin, Globe, Phone } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import { getBusinessProfile, updateBusinessProfile } from '../../../services/api/business';
import { getImageUrl } from '../../../utils/imageUtils';
import styles from './StepComponents.module.css';

const BusinessProfile = ({ user, formData, setFormData, onNext, isSubmitting }) => {
  const { t } = useLocalization();
  const { success, error } = useAlert();

  const [businessData, setBusinessData] = useState({
    businessName: '',
    legalName: '',
    businessAddress: '',
    businessPhone: '',
    website: '',
    description: '',
    logo: null
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);

  // Load business profile data
  useEffect(() => {
    fetchBusinessProfile();
  }, []);

  const fetchBusinessProfile = async () => {
    try {
      setIsLoadingProfile(true);
      const response = await getBusinessProfile();
      const business = response.data;
      
      setBusinessData({
        businessName: business.name || '',
        legalName: business.legalName || '',
        businessAddress: business.businessAddress || '',
        businessPhone: business.businessPhone || '',
        website: business.website || '',
        description: business.description || '',
        logo: null
      });

      if (business.logo) {
        setLogoPreview(getImageUrl(business.logo));
      }
    } catch (err) {
      console.error('Failed to fetch business profile:', err);
      // Fallback to user business data if profile fetch fails
      if (user?.business) {
        setBusinessData({
          businessName: user.business.name || '',
          legalName: user.business.legalName || '',
          businessAddress: user.business.businessAddress || '',
          businessPhone: user.business.businessPhone || '',
          website: user.business.website || '',
          description: user.business.description || '',
          logo: null
        });

        if (user.business.logo) {
          setLogoPreview(getImageUrl(user.business.logo));
        }
      }
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Business Name (required)
    if (!businessData.businessName.trim()) {
      newErrors.businessName = t('setup.businessNameRequired');
    } else if (businessData.businessName.trim().length < 2) {
      newErrors.businessName = t('setup.businessNameMinLength');
    }

    // Legal Name (required)
    if (!businessData.legalName.trim()) {
      newErrors.legalName = t('setup.legalNameRequired');
    } else if (businessData.legalName.trim().length < 2) {
      newErrors.legalName = t('setup.legalNameMinLength');
    }

    // Phone validation (optional)
    if (businessData.businessPhone && !/^[0-9+\-\s()]+$/.test(businessData.businessPhone)) {
      newErrors.businessPhone = t('setup.invalidPhone');
    }

    // Website validation (optional)
    if (businessData.website) {
      try {
        new URL(businessData.website);
      } catch {
        newErrors.website = t('setup.invalidWebsite');
      }
    }

    // Logo validation (optional)
    if (businessData.logo) {
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
      if (!allowedTypes.includes(businessData.logo.type)) {
        newErrors.logo = t('setup.invalidLogoFormat');
      } else if (businessData.logo.size > 2 * 1024 * 1024) { // 2MB (matching backend)
        newErrors.logo = t('setup.logoTooLarge');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBusinessData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBusinessData(prev => ({
        ...prev,
        logo: file
      }));

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setLogoPreview(e.target.result);
      reader.readAsDataURL(file);

      if (errors.logo) {
        setErrors(prev => ({
          ...prev,
          logo: ''
        }));
      }
    }
  };

  const handleRemoveLogo = () => {
    setBusinessData(prev => ({
      ...prev,
      logo: null
    }));
    
    // Reset to original logo if it exists, otherwise clear
    const originalBusiness = user?.business;
    if (originalBusiness?.logo) {
      setLogoPreview(getImageUrl(originalBusiness.logo));
    } else {
      setLogoPreview(null);
    }

    // Reset file input
    const fileInput = document.getElementById('logoInput');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsUpdating(true);

    try {
      // Prepare form data for multipart/form-data
      const submitData = new FormData();
      submitData.append('businessName', businessData.businessName.trim());
      submitData.append('legalName', businessData.legalName.trim());
      
      if (businessData.businessAddress.trim()) {
        submitData.append('businessAddress', businessData.businessAddress.trim());
      }
      
      if (businessData.businessPhone.trim()) {
        submitData.append('businessPhone', businessData.businessPhone.trim());
      }
      
      if (businessData.website.trim()) {
        submitData.append('website', businessData.website.trim());
      }
      
      if (businessData.description.trim()) {
        submitData.append('description', businessData.description.trim());
      }

      // Only append logo if a new file was selected
      if (businessData.logo) {
        submitData.append('logo', businessData.logo);
      }

      const response = await updateBusinessProfile(submitData);
      
      // Update form data for next steps
      setFormData(prev => ({
        ...prev,
        businessProfile: {
          ...businessData,
          updatedBusiness: response.data
        }
      }));

      success(t('setup.businessProfileUpdated'));
      onNext();
    } catch (err) {
      console.error('Business profile update failed:', err);
      if (err.response?.data?.message) {
        error(err.response.data.message);
      } else {
        error(t('setup.businessUpdateError'));
      }
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoadingProfile) {
    return (
      <div className={styles.stepContainer}>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
          <h3>{t('setup.loadingProfile')}</h3>
          <p>{t('setup.loadingProfileMessage')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.stepContainer}>
      {/* <div className={styles.stepHeader}>
        <Building className={styles.stepIcon} size={48} />
        <h2>{t('setup.businessProfileTitle')}</h2>
        <p>{t('setup.businessProfileDescription')}</p>
      </div> */}

      <form onSubmit={handleSubmit} className={styles.stepForm}>
        {/* Business Name (Required) */}
        <div className={styles.formGroup}>
          <label htmlFor="businessName">{t('setup.businessName')} *</label>
          <input
            type="text"
            id="businessName"
            name="businessName"
            value={businessData.businessName}
            onChange={handleInputChange}
            placeholder={t('setup.businessNamePlaceholder')}
            className={errors.businessName ? styles.error : ''}
            required
            autoFocus
          />
          {errors.businessName && <span className={styles.errorText}>{errors.businessName}</span>}
        </div>

        {/* Legal Name (Required) */}
        <div className={styles.formGroup}>
          <label htmlFor="legalName">{t('setup.legalName')} *</label>
          <input
            type="text"
            id="legalName"
            name="legalName"
            value={businessData.legalName}
            onChange={handleInputChange}
            placeholder={t('setup.legalNamePlaceholder')}
            className={errors.legalName ? styles.error : ''}
            required
          />
          {errors.legalName && <span className={styles.errorText}>{errors.legalName}</span>}
          <span className={styles.formHint}>{t('setup.legalNameHint')}</span>
        </div>

        {/* Business Address */}
        <div className={styles.formGroup}>
          <label htmlFor="businessAddress">{t('setup.businessAddress')}</label>
          <div className={styles.inputWithIcon}>
            <MapPin className={styles.inputIcon} size={18} />
            <input
              type="text"
              id="businessAddress"
              name="businessAddress"
              value={businessData.businessAddress}
              onChange={handleInputChange}
              placeholder={t('setup.businessAddressPlaceholder')}
            />
          </div>
        </div>

        {/* Contact Information */}
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="businessPhone">{t('setup.businessPhone')}</label>
            <div className={styles.inputWithIcon}>
              <Phone className={styles.inputIcon} size={18} />
              <input
                type="tel"
                id="businessPhone"
                name="businessPhone"
                value={businessData.businessPhone}
                onChange={handleInputChange}
                placeholder={t('setup.businessPhonePlaceholder')}
                className={errors.businessPhone ? styles.error : ''}
              />
            </div>
            {errors.businessPhone && <span className={styles.errorText}>{errors.businessPhone}</span>}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="website">{t('setup.businessWebsite')}</label>
            <div className={styles.inputWithIcon}>
              <Globe className={styles.inputIcon} size={18} />
              <input
                type="url"
                id="website"
                name="website"
                value={businessData.website}
                onChange={handleInputChange}
                placeholder="https://example.com"
                className={errors.website ? styles.error : ''}
              />
            </div>
            {errors.website && <span className={styles.errorText}>{errors.website}</span>}
          </div>
        </div>

        {/* Description */}
        <div className={styles.formGroup}>
          <label htmlFor="description">{t('setup.businessDescription')}</label>
          <textarea
            id="description"
            name="description"
            value={businessData.description}
            onChange={handleInputChange}
            placeholder={t('setup.businessDescriptionPlaceholder')}
            rows="3"
            className={styles.textarea}
          />
          <span className={styles.formHint}>
            {t('setup.businessDescriptionHint')}
          </span>
        </div>

        {/* Logo Upload */}
        <div className={styles.formGroup}>
          <label>{t('setup.businessLogo')}</label>
          <div className={styles.logoUploadSection}>
            {!logoPreview ? (
              <div 
                className={styles.logoUploadArea} 
                onClick={() => document.getElementById('logoInput').click()}
              >
                <Upload size={32} className={styles.uploadIcon} />
                <p className={styles.uploadText}>{t('setup.uploadLogo')}</p>
                <p className={styles.uploadHint}>{t('setup.logoFormats')}</p>
              </div>
            ) : (
              <div className={styles.logoPreview}>
                <img 
                  src={logoPreview} 
                  alt="Business logo preview" 
                  className={styles.previewImage}
                />
                <button
                  type="button"
                  className={styles.removeLogoButton}
                  onClick={handleRemoveLogo}
                >
                  <X size={16} />
                </button>
              </div>
            )}
            
            <input
              id="logoInput"
              type="file"
              accept=".png,.jpg,.jpeg,.webp"
              onChange={handleLogoChange}
              style={{ display: 'none' }}
            />
            
            {errors.logo && <span className={styles.errorText}>{errors.logo}</span>}
            <span className={styles.formHint}>{t('setup.logoSizeHint')}</span>
          </div>
        </div>

        <div className={styles.stepActions}>
          <button
            type="submit"
            disabled={isUpdating || isSubmitting}
            className={styles.primaryButton}
          >
            {isUpdating ? (
              <>
                <div className={styles.loadingSpinner}></div>
                {t('setup.updating')}
              </>
            ) : (
              <>
                <Building size={20} />
                {t('setup.saveBusinessProfile')}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BusinessProfile;